import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import FlexControlledSelectSeparatedLabel from "../../../components/controlled/FlexControlledSelectSeparatedLabel";
import NewControlledTextFieldSeparatedLabel from "../../../components/controlled/FlexControlledTextFieldSeparatedLabel";

import { Button, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import CompanyBasicInfoSkeleton from "../../../components/skeletons/CompanyBasicInfoSkeleton";
import {
	useGetCompanyWithIdQuery,
	useUpdateCompanyWithIdMutation,
} from "../../../features/companies/companiesApiSlice";
import { useGetCountryCodesQuery } from "../../cargo/cargoApiSlice";

const BasicInfo = ({ companyId }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [updateBasicInfo, { isLoading: isUpdating }] =
		useUpdateCompanyWithIdMutation();
	const {
		data: company,
		error,
		isLoading,
	} = useGetCompanyWithIdQuery(companyId);
	const { data: countryCodes, isLoading: isLoadingCountryCodes } =
		useGetCountryCodesQuery();

	const {
		handleSubmit,
		control,
		setValue,
		formState: { errors },
	} = useForm({
		// resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	useEffect(() => {
		if (company) {
			setValue("name", company.name);
			setValue("nip", company.nip);
			setValue("country", company.country);
			setValue("postalCode", company.postalCode);
			setValue("city", company.city);
			setValue("street", company.street);
		}
	}, [company, setValue]);

	const onSubmit = async ({ name, nip, country, postalCode, city, street }) => {
		await updateBasicInfo({
			companyId,
			data: { name, nip, country, postalCode, city, street },
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_data"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	if (isLoading) {
		return <CompanyBasicInfoSkeleton />;
	}

	return (
		<Box
			component="form"
			noValidate
			onSubmit={handleSubmit(onSubmit)}
			sx={{ maxWidth: "1300px" }}
		>
			<Grid container spacing={0}>
				<Grid container columnSpacing={5} rowSpacing={0} size={12}>
					<Grid
						sx={{ display: "flex", alignItems: "center" }}
						size={{
							xs: 12,
							sm: 8,
							md: 3,
						}}
					>
						<Box
							sx={{
								display: "flex",
								gap: "10px",
								alignItems: "center",
								width: "100%",
							}}
						>
							<Typography sx={{ flex: "0" }}>{t("status")}: </Typography>
							<Box
								sx={{
									background: "#C6F0FF",
									py: 1,
									px: 2,
									flex: "1",
									textAlign: "center",
								}}
							>
								{company.register_source === "form"
									? t("registration")
									: t("contact")}
							</Box>
						</Box>
					</Grid>
					<Grid
						size={{
							xs: 12,
							sm: 8,
							md: 6,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="name"
							label={t("company")}
							required
							error={!!errors?.name}
							fullWidth
							autoFocus
						/>
					</Grid>
					<Grid
						size={{
							xs: 12,
							sm: 4,
							md: 3,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="nip"
							label={t("nip_label")}
							required
							error={!!errors?.nip}
							fullWidth
						/>
					</Grid>
				</Grid>
				<Grid container columnSpacing={5} rowSpacing={0} size={12}>
					<Grid
						size={{
							xs: 12,
							sm: 6,
							md: 1.5,
						}}
					>
						{isLoadingCountryCodes ? (
							<Skeleton
								width={127.5}
								height={36.71}
								sx={{
									transform: "scale(1)",
									marginTop: "16px",
									marginBottom: "8px",
								}}
							/>
						) : (
							<FlexControlledSelectSeparatedLabel
								control={control}
								margin="normal"
								name="country"
								label={t("country")}
								required
								defaultValue=""
								options={countryCodes.map((code) => {
									return { value: code, label: code };
								})}
							/>
						)}
					</Grid>
					<Grid
						size={{
							xs: 12,
							sm: 6,
							md: 2.5,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="postalCode"
							label={t("postal_code_label")}
							required
							error={!!errors?.postalCode}
							fullWidth
						/>
					</Grid>
					<Grid
						size={{
							xs: 12,
							sm: 6,
							md: 3,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="city"
							label={t("city_label")}
							required
							error={!!errors?.city}
							fullWidth
						/>
					</Grid>
					<Grid
						size={{
							xs: 12,
							sm: 6,
							md: 4,
						}}
					>
						<NewControlledTextFieldSeparatedLabel
							shrinkLabel={false}
							control={control}
							margin="normal"
							name="street"
							label={t("street")}
							required
							error={!!errors?.street}
							fullWidth
						/>
					</Grid>
					<Grid
						size={{
							xs: 12,
							sm: 6,
							md: 1,
						}}
					>
						<Button
							loading={isUpdating}
							type="submit"
							fullWidth
							variant="contained"
						>
							{t("save")}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};

export default BasicInfo;
